import React, { useState, useEffect } from 'react';
import { 
  Share2,
  Instagram,
  Facebook,
  MessageCircle,
  TrendingUp,
  Heart,
  MessageSquare,
  Eye,
  Loader2,
  RefreshCw,
  Plus,
  Calendar,
  Clock
} from 'lucide-react';
import { apiRequest } from '../../lib/utils';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

const PLATFORMS = {
  instagram: { icon: Instagram, color: 'bg-gradient-to-br from-purple-500 to-pink-500', name: 'Instagram' },
  facebook: { icon: Facebook, color: 'bg-blue-600', name: 'Facebook' },
  whatsapp: { icon: MessageCircle, color: 'bg-green-500', name: 'WhatsApp' }
};

export default function SocialMediaHubWidget({ showHeader = true }) {
  const [stats, setStats] = useState({});
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSocialData();
  }, []);

  const fetchSocialData = async () => {
    try {
      const data = await apiRequest('/sales/social-media-stats');
      setStats(data.stats || {});
      setScheduledPosts(data.scheduled_posts || []);
    } catch (error) {
      console.error('Failed to fetch social data:', error);
      setStats(getMockStats());
      setScheduledPosts(getMockScheduledPosts());
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm h-full flex flex-col" data-testid="social-media-hub-widget">
      {showHeader && (
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-orange-500 flex items-center justify-center">
              <Share2 size={20} className="text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Social Media Hub</h3>
              <p className="text-xs text-slate-500">Engagement & Scheduling</p>
            </div>
          </div>
          <Button variant="ghost" size="sm">
            <Plus size={16} />
          </Button>
        </div>
      )}

      {/* Platform Stats */}
      <div className="p-4 border-b border-slate-100">
        <div className="grid grid-cols-3 gap-3">
          {Object.entries(PLATFORMS).map(([key, platform]) => {
            const platformStats = stats[key] || { followers: 0, engagement: 0, growth: 0 };
            const Icon = platform.icon;
            return (
              <div key={key} className="text-center">
                <div className={`w-10 h-10 rounded-xl ${platform.color} flex items-center justify-center mx-auto mb-2`}>
                  <Icon size={18} className="text-white" />
                </div>
                <p className="text-lg font-bold text-slate-900">{formatNumber(platformStats.followers)}</p>
                <p className="text-xs text-slate-500">followers</p>
                <p className={`text-xs font-medium mt-1 ${platformStats.growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {platformStats.growth >= 0 ? '+' : ''}{platformStats.growth}%
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Weekly Engagement Summary */}
      <div className="p-4 border-b border-slate-100">
        <p className="text-xs font-medium text-slate-500 mb-3">THIS WEEK'S ENGAGEMENT</p>
        <div className="grid grid-cols-4 gap-2">
          <div className="text-center p-2 bg-slate-50 rounded-lg">
            <Eye size={16} className="mx-auto text-slate-400 mb-1" />
            <p className="text-sm font-bold text-slate-900">{formatNumber(stats.weekly?.reach || 0)}</p>
            <p className="text-xs text-slate-500">Reach</p>
          </div>
          <div className="text-center p-2 bg-red-50 rounded-lg">
            <Heart size={16} className="mx-auto text-red-400 mb-1" />
            <p className="text-sm font-bold text-slate-900">{formatNumber(stats.weekly?.likes || 0)}</p>
            <p className="text-xs text-slate-500">Likes</p>
          </div>
          <div className="text-center p-2 bg-blue-50 rounded-lg">
            <MessageSquare size={16} className="mx-auto text-blue-400 mb-1" />
            <p className="text-sm font-bold text-slate-900">{formatNumber(stats.weekly?.comments || 0)}</p>
            <p className="text-xs text-slate-500">Comments</p>
          </div>
          <div className="text-center p-2 bg-green-50 rounded-lg">
            <Share2 size={16} className="mx-auto text-green-400 mb-1" />
            <p className="text-sm font-bold text-slate-900">{formatNumber(stats.weekly?.shares || 0)}</p>
            <p className="text-xs text-slate-500">Shares</p>
          </div>
        </div>
      </div>

      {/* Scheduled Posts */}
      <div className="flex-1 overflow-y-auto p-4">
        <p className="text-xs font-medium text-slate-500 mb-3">SCHEDULED POSTS</p>
        <div className="space-y-2">
          {scheduledPosts.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">No scheduled posts</p>
          ) : (
            scheduledPosts.map((post, idx) => {
              const platform = PLATFORMS[post.platform] || PLATFORMS.instagram;
              const Icon = platform.icon;
              return (
                <div key={idx} className="flex items-center gap-3 p-2 rounded-lg border border-slate-200 hover:bg-slate-50">
                  <div className={`w-8 h-8 rounded-lg ${platform.color} flex items-center justify-center`}>
                    <Icon size={14} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{post.title}</p>
                    <p className="text-xs text-slate-500 flex items-center gap-1">
                      <Clock size={10} /> {post.scheduled_time}
                    </p>
                  </div>
                  <Badge variant="outline" className="text-xs">{post.status}</Badge>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

function formatNumber(num) {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toString();
}

function getMockStats() {
  return {
    instagram: { followers: 12500, engagement: 4.2, growth: 8.5 },
    facebook: { followers: 8200, engagement: 2.8, growth: 3.2 },
    whatsapp: { followers: 5600, engagement: 15.5, growth: 12.1 },
    weekly: { reach: 45000, likes: 3200, comments: 480, shares: 890 }
  };
}

function getMockScheduledPosts() {
  return [
    { platform: 'instagram', title: 'Diwali Sale Announcement', scheduled_time: 'Today, 6:00 PM', status: 'ready' },
    { platform: 'facebook', title: 'Weekly Specials Video', scheduled_time: 'Tomorrow, 10:00 AM', status: 'ready' },
    { platform: 'whatsapp', title: 'Flash Sale Alert', scheduled_time: 'Tomorrow, 2:00 PM', status: 'draft' },
    { platform: 'instagram', title: 'New Product Showcase', scheduled_time: 'Mar 25, 5:00 PM', status: 'ready' }
  ];
}
